import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";

interface Stat {
  value: number;
  suffix: string;
  label: string;
  decimals?: number;
}

const stats: Stat[] = [
  { value: 450, suffix: "+", label: "Rentals fulfilled", decimals: 0 },
  { value: 50, suffix: "+", label: "Events supported", decimals: 0 },
  { value: 20, suffix: "+", label: "Cities served for events", decimals: 0 },
  { value: 4.9, suffix: "", label: "Customer rating", decimals: 1 },
];

function CountUp({ value, decimals }: { value: number; decimals: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1400, bounce: 0 });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);

  useEffect(() => {
    const unsub = spring.on("change", (latest) => {
      setDisplay(latest.toFixed(decimals));
    });
    return () => unsub();
  }, [spring, decimals]);

  return <span ref={ref}>{display}</span>;
}

const StatsSection = () => {
  return (
    <section className="border-t border-border bg-background py-16 md:py-24 px-6">
      <div className="max-w-narrative mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="font-body text-sm uppercase tracking-[0.3em] text-secondary mb-4 block">
            Trusted by hundreds
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground">
            Experience VR With Confidence
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 md:divide-x md:divide-border">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="flex flex-col items-center text-center px-4 py-6 md:py-8"
            >
              <div className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gradient leading-none">
                <CountUp value={stat.value} decimals={stat.decimals ?? 0} />
                {stat.suffix}
              </div>
              <p className="font-body text-sm text-muted-foreground mt-3">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
